import { useEffect, useRef, useState } from "react";

import { SaveButtonStatusType } from "types/apps-container";

import { DataGrid } from "devextreme-react";
import {
    Column,
    CustomRule,
    Editing,
    Lookup,
    RequiredRule,
    RowDragging,
    Scrolling,
} from "devextreme-react/data-grid";
import GoAiguaTextInput from "./goaiguatextinput";
import ModalContentLayout from "./modal-content-layout.component";
import EditAppsModalActions from "components/adhoc/edit-apps-modal-actions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/pro-light-svg-icons";

import { useAppsContainerTabContext } from "storage/context/apps-container-tab-context";

import { makeEditAppsModalFunctions } from "utils/components/edit-apps-modal-funcions";
import { useTranslation } from "utils/hooks/use-translation.hook";
import { makeDeepCopy } from "utils/functions";

type EditAppsModalType = {
    updateData: () => Promise<void>;
    closeModalfn: () => void;
};

const EditAppsModal = ({ updateData, closeModalfn }: EditAppsModalType) => {
    const {
        isImplantUser,
        containers,
        editModalData,
        editModalGroupName,
        editMode,
        containerId,
        editTableRef,
        setModalContent,
        setContainerId,
        setError,
        subscribeContent,
        unsubscribeContent,
        setEditMode,
        setContainers,
    } = useAppsContainerTabContext();

    const [groupNameInput, setGroupNameInput] = useState(editModalGroupName);
    const [groupData, setGroupData] = useState(makeDeepCopy(editModalData));
    const [itemsToDeleteIdList, setItemsToDeleteIdList] = useState<number[]>(
        []
    );
    const [saveButtonStatus, setSaveButtonStatus] =
        useState<SaveButtonStatusType>("disabled");

    const translate = useTranslation();
    const dataRef = useRef(editModalData);

    const {
        availableOptions,
        requiredField,
        tableLabels,
        visibilityOptions,
        changeGroupVisibility,
        handleItemHasBeenReordered,
        defineSaveChangesOnRepoStrategy,
        openEditSubgroupModal,
        saveSubGroupTableChangesTemporary,
        defineSaveButtonStatus,
        defineTextBasedOnEditMode,
        hasGoaiguaContent,
        handleInputChange,
        handleRemoveRow,
        dxCustomValidation,
    } = makeEditAppsModalFunctions({
        containers,
        dataRef,
        editMode,
        groupData,
        editModalGroupName,
        groupNameInput,
        containerId,
        itemsToDeleteIdList,
        setSaveButtonStatus,
        setItemsToDeleteIdList,
        setContainerId,
        setContainers,
        closeModalfn,
        setEditMode,
        setError,
        setGroupData,
        setGroupNameInput,
        subscribeContent,
        unsubscribeContent,
        updateData,
        translate,
    });

    const renderActionsColumnCaption = () => {
        if (editMode === "group") return;

        const editHeader: HTMLCollection =
            document.getElementsByClassName("dx-command-edit");

        if (editHeader) {
            editHeader[0].innerHTML = `
                <div class="dx-treelist-text-content">${translate(
                    "app.general.actions"
                )}</div>
            `;
        }
    };

    useEffect(() => {
        renderActionsColumnCaption();
    });

    useEffect(() => {
        setSaveButtonStatus(defineSaveButtonStatus());
    }, [
        groupData,
        dataRef.current,
        groupNameInput,
        editModalGroupName,
        editMode,
    ]);

    useEffect(() => {
        if (editMode !== "app" || !editTableRef.current) return;
        editTableRef.current.instance.addRow();

        editTableRef.current.instance.focus(
            editTableRef.current.instance.getCellElement(
                editModalData.length,
                "icon"
            ) as any
        );
    }, [editMode]);

    return (
        <ModalContentLayout
            firstButtonStatus="enabled"
            secondButtonStatus={saveButtonStatus}
            firstButtonTitle={translate("app.gis.cancel")}
            secondButtonTitle={translate("app.users.save")}
            onFirstButtonClick={closeModalfn}
            onSecondButtonClick={defineSaveChangesOnRepoStrategy()}
        >
            <div className="c-edit-apps-modal">
                <div className="c-edit-apps-modal__header">
                    <GoAiguaTextInput
                        text={groupNameInput}
                        onChange={handleInputChange}
                        id="edit-app-name"
                        maxLength={48}
                        label={defineTextBasedOnEditMode(
                            "app.welcome.page.edit.modal.input.label"
                        )}
                    />

                    {!hasGoaiguaContent() && (
                        <button
                            onClick={() => setModalContent("delete")}
                            className="c-edit-apps-modal__delete-button"
                        >
                            <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                    )}
                </div>

                <section
                    className={`c-edit-apps-modal__table-container ${
                        editMode !== "group"
                            ? "c-edit-apps-modal__table-container--full"
                            : ""
                    }`}
                >
                    <h3 className="c-edit-apps-modal__apps-title">
                        {defineTextBasedOnEditMode(
                            "app.welcome.page.edit.modal.table.title"
                        )}
                    </h3>

                    <DataGrid
                        dataSource={groupData}
                        allowColumnReordering={false}
                        showBorders={false}
                        rowAlternationEnabled={true}
                        showColumnLines={false}
                        showRowLines={false}
                        columnAutoWidth={false}
                        noDataText={""}
                        onContentReady={renderActionsColumnCaption}
                        onSaved={saveSubGroupTableChangesTemporary}
                        onRowRemoving={handleRemoveRow}
                        ref={editTableRef}
                    >
                        <RowDragging
                            data={groupData}
                            onReorder={handleItemHasBeenReordered}
                            allowReordering={true}
                        />
                        <Scrolling mode="virtual" />

                        <Column
                            dataField="title"
                            caption={translate("app.general.name")}
                            dataType="string"
                        >
                            <RequiredRule message={requiredField} />
                        </Column>

                        {editMode !== "group" ? (
                            <Column
                                dataField="icon"
                                caption={translate("app.general.icon")}
                            ></Column>
                        ) : null}

                        {editMode !== "group" ? (
                            <Column dataField="url" caption={"URL"}></Column>
                        ) : null}

                        {hasGoaiguaContent() && editMode !== "group" ? (
                            <Column
                                dataField="installed"
                                caption={translate("app.users.status")}
                            >
                                <Lookup
                                    dataSource={availableOptions}
                                    valueExpr={"value"}
                                    displayExpr={"label"}
                                />
                                <CustomRule
                                    message={requiredField}
                                    validationCallback={dxCustomValidation}
                                />
                            </Column>
                        ) : null}

                        {editMode !== "group" ? (
                            <Column
                                dataField="visible"
                                caption={translate("app.general.visibility")}
                            >
                                <Lookup
                                    dataSource={visibilityOptions}
                                    valueExpr={"value"}
                                    displayExpr={"label"}
                                />
                                <CustomRule
                                    message={requiredField}
                                    validationCallback={dxCustomValidation}
                                />
                            </Column>
                        ) : null}

                        {isImplantUser && editMode !== "group" ? (
                            <Column dataField="goAigua" caption={"GoAigua"}>
                                <Lookup
                                    dataSource={visibilityOptions}
                                    valueExpr={"value"}
                                    displayExpr={"label"}
                                />
                            </Column>
                        ) : null}

                        {editMode === "group" ? (
                            <Column
                                dataField="alias"
                                caption={translate("app.general.actions")}
                                dataType="string"
                                cellRender={({ data }) => (
                                    <EditAppsModalActions
                                        {...{
                                            data,
                                            editMode,
                                            openEditSubgroupModal,
                                            changeGroupVisibility,
                                        }}
                                    />
                                )}
                            />
                        ) : (
                            <Editing
                                allowAdding
                                allowUpdating
                                allowDeleting={!hasGoaiguaContent()}
                                confirmDelete={false}
                                useIcons
                                newRowPosition={"last"}
                                mode={"row"}
                                texts={tableLabels}
                            />
                        )}
                    </DataGrid>
                </section>
            </div>
        </ModalContentLayout>
    );
};

export default EditAppsModal;
